import { CustomIcon } from "components/icon/customIcon.component";
import { Text } from "components/Typography/text.styled";
import { Color } from "config/colors";
import { useTranslation } from "react-i18next";
import {
  OrgStructureContentContainer,
  OrgStructureEmptyStateContainer,
} from "./organizationalStructure.styled";
import { FontFamily, FontSize } from "config/font";
import { useState } from "react";
import { CreateModal } from "./createModal.component";
import { InfoModal as Info } from "./infoModal.component";
import { ChooseWorkersModal } from "./chooseWorkersModal.component";
import type { Worker as ChooseWorkersModalWorker } from "./chooseWorkersModal.types";
import { ButtonComponent } from "../general/button/button.component";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useOrganizationalStructureStore } from "web-apps/company/stores/organizationalStructureStore/organizationalStructureStore";
import { InfoModal } from "../general/infoModal/infoModal.component";

type Props = {
  type: "Entity" | "Unit" | "Team" | "Worker";
  id?: number;
  parentId?: number;
};

export const EmptyStructure: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [error, setError] = useState(false);
  const [companyState] = useCompanyStore();
  const [, organizationalStructureActions] = useOrganizationalStructureStore();

  const isWorker = props.type === "Worker";
  const isEntity = props.type === "Entity";
  const isUnit = props.type === "Unit";

  const getHeadingText = () => {
    switch (props.type) {
      case "Entity":
        return t("OrganizationalStructure.OrganizationalStructure");
      case "Unit":
        return t("OrganizationalStructure.Unit");
      case "Worker":
        return t("OrganizationalStructure.NoWorkers");
      default:
        return t("OrganizationalStructure.NoTeams");
    }
  };

  const getInfoText = () => {
    switch (props.type) {
      case "Entity":
        return t(
          "OrganizationalStructure.YouHaveNotCreatetOrganizationalStructure"
        );
      case "Unit":
        return t("OrganizationalStructure.YouHaveNotCreatedUnits");
      case "Worker":
        return t("OrganizationalStructure.NoWorkersInThisTeam");
      default:
        return t("OrganizationalStructure.NoTeamsInThisUnit");
    }
  };

  const getButtonText = () => {
    return isWorker
      ? t("OrganizationalStructure.AssignToTeam")
      : t(`OrganizationalStructure.Create${props.type}`);
  };

  const handlePrimaryButtonClick = () => {
    if (isWorker) {
      setShowAssignModal(true);
    } else {
      setShowCreateModal(true);
    }
  };

  const assignWorkers = async (
    checked: ChooseWorkersModalWorker[],
  ): Promise<void> => {
    if (!companyState.company?.id || checked.length === 0 || !props.id) return;
    try {
      await Promise.all(
        checked.map((worker) =>
          organizationalStructureActions.assignWorkerToTeam(
            companyState.company!.id,
            props.id!,
            worker.workerId
          )
        )
      );
      setError(false);
      setShowInfo(true);
    } catch (e) {
      setError(true);
      setShowInfo(true);
    }
  };
  const shouldShowInfoButton = isEntity || isUnit;

  const closeModal = () => {
    setShowInfo(false);
    setError(false);
    setShowAssignModal(false);
  };

  return (
    <OrgStructureEmptyStateContainer>
      <OrgStructureContentContainer isEntity={isEntity}>
        <CustomIcon
          name={""}
          color={Color.DarkGrey}
          size="53px"
          padding="12px"
        />
        <Text
          fontSize={isWorker ? FontSize.Standard : FontSize.H4}
          fontFamily={FontFamily.MontserratSemiBold}
        >
          {getHeadingText()}
        </Text>

        {(!isWorker || (isWorker && props.parentId !== -1)) && (
          <Text
            fontSize={isWorker ? FontSize.Small : FontSize.Standard}
            style={{ width: 400 }}
          >
            {getInfoText()}
          </Text>
        )}

        {(!isWorker || (isWorker && props.parentId !== -1)) && (
          <ButtonComponent
            size={"small"}
            onClick={handlePrimaryButtonClick}
            action={true}
            marginTop={10}
            marginBottom={isEntity ? 700 : 0}
            width={isEntity ? 350 : 150}
          >
            {getButtonText()}
          </ButtonComponent>
        )}
        {shouldShowInfoButton && (
          <ButtonComponent
            size={"small"}
            onClick={() => setShowInfoModal(true)}
            marginTop={10}
            marginBottom={isEntity ? 700 : 0}
            width={isEntity ? 350 : 200}
          >
            {t("OrganizationalStructure.WhatIsOrganizationalStructure")}
          </ButtonComponent>
        )}
      </OrgStructureContentContainer>
      <Info showInfoModal={showInfoModal} setShowInfoModal={setShowInfoModal} />
      {!isWorker && (
        <CreateModal
          type={props.type as "Entity" | "Unit" | "Team"}
          showCreateModal={showCreateModal}
          setShowCreateModal={setShowCreateModal}
          id={props.id}
        />
      )}
      {showAssignModal && props.id && (
        <ChooseWorkersModal
          setShowModal={setShowAssignModal}
          showModal={showAssignModal}
          onClick={assignWorkers}
          title={t("OrganizationalStructure.AssignWorkers")}
          info={t("OrganizationalStructure.AssignWorkersInfo")}
          buttonTitle={t("OrganizationalStructure.Assign")}
          filter={'All'}
        />
      )}
      {showInfo && (
        <InfoModal
          isOpen={showInfo}
          title={
            error
              ? t("OrganizationalStructure.SomethingWentWrong")
              : t("OrganizationalStructure.Assigned")
          }
          message={
            error
              ? t("OrganizationalStructure.SomethingWentWrongInfo")
              : t("OrganizationalStructure.AssignedInfo")
          }
          confirmText={t("OrganizationalStructure.Ok")}
          onConfirm={closeModal}
          onCancel={closeModal}
        />
      )}
    </OrgStructureEmptyStateContainer>
  );
};
